type RootStateLike = {
  company?: {
    selectedCompanyId?: number | null;
  };
};

const COMPANY_STORAGE_KEY = "selected_company";

export function getAuthToken(): string | null {
  try {
    return localStorage.getItem("auth_token");
  } catch {
    return null;
  }
}

function getAuthTokenType(): string {
  try {
    return localStorage.getItem("auth_token_type") || "Bearer";
  } catch {
    return "Bearer";
  }
}

/**
 * Refresh token for `AuthService.RefreshToken`.
 *
 * Absent for sessions created before refresh tokens existed and for the
 * impersonation flow, which writes only an access token — both keep working
 * until their access token runs out.
 */
export function getRefreshToken(): string | null {
  try {
    return localStorage.getItem("auth_refresh_token");
  } catch {
    return null;
  }
}

/**
 * Writes both tokens in one call.
 *
 * The server rotates the refresh token on every refresh, so storing a new
 * access token while keeping the old refresh token is not an option: the server
 * has already spent that pair, and replaying the old refresh token reads as a
 * stolen session.
 */
export function setAuthTokens({
  accessToken,
  tokenType,
  refreshToken,
}: {
  accessToken: string;
  tokenType?: string;
  refreshToken?: string;
}): void {
  try {
    localStorage.setItem("auth_token", accessToken);
    localStorage.setItem("auth_token_type", tokenType || "Bearer");

    if (refreshToken) {
      localStorage.setItem("auth_refresh_token", refreshToken);
    } else {
      localStorage.removeItem("auth_refresh_token");
    }
  } catch (error) {
    console.error("Failed to persist auth tokens:", error);
  }
}

export function getAuthorizationHeaderValue(): string | null {
  const token = getAuthToken();
  if (!token) return null;
  return `${getAuthTokenType()} ${token}`;
}

function parsePersistedCompanyState(raw: string | null): number | null {
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as {
      selectedCompanyId?: unknown;
    };

    const selectedCompanyId = Number(parsed.selectedCompanyId);
    return Number.isFinite(selectedCompanyId) && selectedCompanyId > 0 ? selectedCompanyId : null;
  } catch {
    return null;
  }
}

export function getPersistedSelectedCompanyId(): number | null {
  try {
    return parsePersistedCompanyState(localStorage.getItem(COMPANY_STORAGE_KEY));
  } catch {
    return null;
  }
}

export function resolveSelectedCompanyId(state?: RootStateLike): number | null {
  const stateCompanyId = Number(state?.company?.selectedCompanyId);
  if (Number.isFinite(stateCompanyId) && stateCompanyId > 0) {
    return stateCompanyId;
  }

  return getPersistedSelectedCompanyId();
}

export function clearAuthTokens(): void {
  try {
    localStorage.removeItem("auth_token");
    localStorage.removeItem("auth_token_type");
    localStorage.removeItem("auth_refresh_token");
  } catch (error) {
    console.error("Failed to clear auth token:", error);
  }
}

export function redirectToLoginIfNeeded(): void {
  if (typeof window === "undefined") return;

  const currentPath = window.location.pathname;
  const isLoginPage = currentPath.includes("/login") || currentPath.includes("/login-otp");
  if (!isLoginPage) {
    window.location.href = "/login";
  }
}
